import React, { useState } from "react";
import { ShieldAlert, MessageSquare, CheckCircle2, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { incidentes as api } from "../../api/modulos.service";
import { coincide } from "../../utils/texto";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";
import CapturaFotos from "./CapturaFotos";

/**
 * INCIDENTES.
 *
 * Lo levanta el guardia —es quien lo ve— y la administración lo cierra. Nada
 * se reescribe: la corrección es un comentario más, con su autor y su hora.
 * Un parte que se puede editar a posteriori no sirve como respaldo de nada.
 */

const FORM_VACIO = { type: "persona_sospechosa", description: "", severity: "media", place: "", peopleInvolved: "", plates: "", houseNumber: "", polygon: "" };

const COLOR = { abierto: "var(--danger)", en_proceso: "var(--warning)", cerrado: "var(--text-tertiary)" };

const fecha = (f) =>
  new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "short", timeStyle: "short" });

export default function Incidentes({ rol, catalogos, modulos }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [creado, setCreado] = useState(null);

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.incidents || [];
  const esAdmin = rol === "admin";

  const filtrados = busqueda
    ? lista.filter((i) => coincide(i.description, busqueda))
    : lista;

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      const r = await api.crear(form);
      setCreado(r.incident);
      setForm(FORM_VACIO);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo registrar");
    } finally {
      setGuardando(false);
    }
  };

  const comentar = async (i) => {
    const texto = window.prompt("Comentario:");
    if (!texto) return;
    await api.comentar(i._id, { text: texto });
    recargar();
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Incidentes"
        subtitulo="Lo que pasó en la entrada y hay que dejar por escrito."
        accion="Reportar incidente"
        alAccionar={() => { setCreado(null); setAbierta(true); }}
        icono={ShieldAlert}
      />

      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar en las descripciones…" />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : filtrados.length === 0 ? (
        <Vacio>No hay incidentes registrados.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {filtrados.map((i) => (
            <div key={i._id} style={{ ...est.tarjeta, borderColor: COLOR[i.status] }}>
              <h3 style={est.tarjetaTitulo}>
                {catalogos?.tiposIncidente?.find((t) => t.valor === i.type)?.etiqueta || i.type}
              </h3>
              <div style={{ ...est.tarjetaDato, color: COLOR[i.status], fontWeight: 600 }}>
                {i.status === "abierto" && "Abierto"}
                {i.status === "en_proceso" && "En proceso"}
                {i.status === "cerrado" && "Cerrado"}
                {" · "}Severidad {i.severity}
              </div>
              <div style={est.tarjetaDato}>{fecha(i.occurredAt)}</div>
              <div style={{ ...est.tarjetaDato, display: "block" }}>{i.description}</div>
              {i.reportedBy?.name && <div style={est.tarjetaDato}>Reportó: {i.reportedBy.name}</div>}
              {i.comments?.length > 0 && (
                <div style={est.tarjetaDato}>{i.comments.length} comentario(s)</div>
              )}

              <div style={est.tarjetaAcciones}>
                {i.status !== "cerrado" && (
                  <Button size="sm" variant="outline" leftIcon={MessageSquare} onClick={() => comentar(i)}>
                    Comentar
                  </Button>
                )}
                {esAdmin && i.status !== "cerrado" && (
                  <Button size="sm" variant="primary" leftIcon={CheckCircle2}
                    onClick={() => api.cerrar(i._id, {}).then(recargar)}>
                    Cerrar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo={creado ? "Incidente registrado" : "Reportar un incidente"}
        abierta={abierta}
        alCerrar={() => { setAbierta(false); setCreado(null); }}
        pie={
          creado ? (
            <Button variant="primary" onClick={() => { setAbierta(false); setCreado(null); }}>Listo</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
              <Button variant="primary" onClick={guardar} loading={guardando}>Reportar</Button>
            </>
          )
        }
      >
        {error && <Aviso>{error}</Aviso>}

        {creado ? (
          <>
            <p style={est.subtitulo}>
              Queda registrado. Si tomó fotografías, adjúntelas ahora: después es más difícil.
            </p>
            {modulos?.fotos ? (
              <CapturaFotos ambito="incidentes" recursoId={creado._id} tipos={[
                { valor: "otro", etiqueta: "Del incidente" },
                { valor: "vehiculo", etiqueta: "Vehículo" },
                { valor: "placa", etiqueta: "Placa" },
              ]} />
            ) : (
              <Aviso>Su residencial tiene desactivadas las fotografías.</Aviso>
            )}
          </>
        ) : (
          <>
            <Seleccion
              etiqueta="Qué pasó"
              value={form.type}
              onChange={cambiar("type")}
              opciones={catalogos?.tiposIncidente || []}
            />
            <Area
              etiqueta="Descripción"
              value={form.description}
              onChange={cambiar("description")}
              placeholder="Cuente lo que ocurrió con el mayor detalle posible"
              required
            />
            <div style={est.fila}>
              <Seleccion
                etiqueta="Severidad"
                value={form.severity}
                onChange={cambiar("severity")}
                opciones={[
                  { valor: "baja", etiqueta: "Baja" },
                  { valor: "media", etiqueta: "Media" },
                  { valor: "alta", etiqueta: "Alta" },
                ]}
              />
              <Entrada etiqueta="Lugar" value={form.place} onChange={cambiar("place")} placeholder="Entrada principal" />
            </div>
            <div style={est.fila}>
              <Entrada etiqueta="Personas involucradas" value={form.peopleInvolved} onChange={cambiar("peopleInvolved")} />
              <Entrada etiqueta="Placas" value={form.plates} onChange={cambiar("plates")} />
            </div>
            <div style={est.fila}>
              <Entrada etiqueta="Polígono" value={form.polygon} onChange={cambiar("polygon")} />
              <Entrada etiqueta="Casa" value={form.houseNumber} onChange={cambiar("houseNumber")} />
            </div>
          </>
        )}
      </Ventana>
    </div>
  );
}
